import React, { Component } from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import { Formik } from "formik";
import * as yup from "yup";
import get from "src/lib/get";
import trackGiftTransaction from "src/analytics/gift-tracking";
import isScaMessageEvent from "src/lib/is-sca-message-event";
import { authenticatePaymentService } from "src/services/cornerstone/payments/authenticate";
import { orderGiftService } from "src/services/cornerstone/orders/gift";
import {
  resetGifts,
  setExpressDelivery as doSetExpressDelivery,
} from "src/redux/gifts/actions";
import { requiresPaymentAuthorisation } from "src/redux/app/actions";
import {
  getGiftsProductsPayload,
  getHasExpressDelivery,
  getGiftsSubTotal,
  getGiftsProductSKUs,
  getGiftsTransactionProducts,
  getIsGiftOffer,
} from "src/redux/gifts/selectors";
import { getPaymentAuthorisationData } from "src/redux/app/selectors";
import { SectionLayout } from "src/react/layout";
import { paymentCardSchema } from "src/react/components/payment-card";
import ScaModal from "src/react/modals/components/sca-modal";
import PaymentAndDeliveryForm from "./payment-and-delivery-form";

const yupString = yup.string().default("").trim();
const schema = yup.object({
  email: yupString.email().label("Email").required(),
  firstName: yupString.label("First Name").required(),
  surname: yupString.label("Surname").required(),
  firstLine: yupString.label("First Line").required().max(30),
  secondLine: yupString.label("Second Line").max(30),
  city: yupString.label("City").required().max(20),
  postcode: yupString
    .label("Postcode")
    .required()
    .max(10)
    .matches(
      /^[A-Za-z0-9]{0,10}[ -]?[A-Za-z0-9]{0,10}$/,
      'Postcode should only have the following: numbers, letters, "-" and "space"'
    ),
  country: yupString.default("United Kingdom").label("Country").required(),
  instructions: yupString.label("Safe Place Details").max(30),
  searchTerm: yupString.label("searchTerm"),
  expressDelivery: yup.boolean().default(false),
  addressList: yup.array().default([]),
  ...paymentCardSchema,
  products: yup
    .array()
    .default([])
    .required(
      "Looks like your basket is empty. You need to add at least one product to complete your checkout."
    ),
});

class GiftsPaymentAndDelivery extends Component {
  static propTypes = {
    setExpressDelivery: PropTypes.func.isRequired,
    resetGifts: PropTypes.func.isRequired,
    requiresPaymentAuthorisation: PropTypes.func.isRequired,
    productsPayload: PropTypes.array,
    hasExpressDelivery: PropTypes.bool,
    paymentError: PropTypes.object,
    paymentAuthorisationData: PropTypes.object,
    defaults: PropTypes.object,
    isGiftOffer: PropTypes.bool,
  };

  static defaultProps = {
    productsPayload: [],
    defaults: {},
  };

  state = {
    isSubmitting: false,
    error: null,
  };

  completeOrder = async (payload, { setStatus, setSubmitting }) => {
    setStatus(null);

    this.setState({
      isSubmitting: true,
      error: null,
    });

    const orderPayload = {
      email: payload.email,
      first_name: payload.firstName,
      surname: payload.surname,
      delivery_address1: payload.firstLine,
      delivery_address2: payload.secondLine,
      delivery_city: payload.city,
      delivery_country: payload.country,
      delivery_postcode: payload.postcode,
      delivery_safe_place: payload.instructions,
      express_delivery: this.props.hasExpressDelivery,
      delivery_method: 2,
      products: payload.products,
      encryptedCardNumber: payload.encryptedCardNumber,
      encryptedExpiryMonth: payload.encryptedExpiryMonth,
      encryptedExpiryYear: payload.encryptedExpiryYear,
      encryptedSecurityCode: payload.encryptedSecurityCode,
    };
    const offer = this.props.isGiftOffer ? "ALLGIFTS20" : null;
    const orderResponse = await orderGiftService({
      payload: orderPayload,
      offer,
    });

    if (orderResponse.error) {
      this.setState({
        isSubmitting: false,
        error: orderResponse.error || { "error-code": "default" },
      });
      setSubmitting(false);
    } else if (orderResponse.data.Authentication === "3DS1") {
      this.props.requiresPaymentAuthorisation(orderResponse.data);
    } else {
      this.authoriseOrder({
        userEmail: payload.email,
        order: orderResponse.data,
        setSubmitting,
      });
    }
  };

  authoriseOrder = async ({ order, userEmail, setSubmitting }) => {
    const { subTotal, transactionProducts, productSKUs, history } = this.props;

    let orderData = order;

    if (order.PaymentData) {
      orderData = await authenticatePaymentService({
        payload: order,
      });

      if (orderData.error) {
        this.setState({
          isSubmitting: false,
          error: orderData.error || { "error-code": "default" },
        });
        setSubmitting(false);
        return false;
      }
    }
    const transaction = get(orderData, "data.transaction.data", {});
    trackGiftTransaction({
      currency: "GBP",
      transactionId: transaction.id,
      transactionTotal: subTotal,
      transactionProducts: transactionProducts,
    });
    history.push({
      pathname: "/gifts-thanks",
      state: {
        transaction: {
          ...transaction,
          productCount: productSKUs.length,
          userEmail,
        },
      },
    });
    this.props.resetGifts();
  };

  handleScaResult = (event) => {
    const data = get(event, "data.scaData", null);
    if (isScaMessageEvent(event)) {
      this.authoriseOrder({
        setSubmitting: () => this.setState({ isSubmitting: false }),
        order: {
          PaymentData: this.props.paymentAuthorisationData.PaymentData,
          ...data,
        },
      });
    }
  };

  render() {
    return (
      <SectionLayout
        data-selector="GiftsCheckout"
        title="Gifts Checkout"
        description="Our boxes are designed to fit through most UK letterboxes for convenient delivery."
      >
        <Formik
          displayName="PaymentAndDeliveryForm"
          enableReinitialize={true}
          validationSchema={schema}
          validateOnChange
          initialValues={{
            ...schema.default(),
            products: this.props.productsPayload,
          }}
          onSubmit={this.completeOrder}
        >
          {(formikProps) => (
            <PaymentAndDeliveryForm
              {...formikProps}
              {...this.props}
              isSubmitting={this.state.isSubmitting}
              paymentError={this.state.error}
            />
          )}
        </Formik>
        {this.state.isSubmitting && this.props.paymentAuthorisationData && (
          <ScaModal
            scaData={this.props.paymentAuthorisationData}
            handleScaResult={this.handleScaResult}
          />
        )}
      </SectionLayout>
    );
  }
}

const mapStateToProps = (state) => ({
  productsPayload: getGiftsProductsPayload(state),
  hasExpressDelivery: getHasExpressDelivery(state),
  subTotal: getGiftsSubTotal(state),
  productSKUs: getGiftsProductSKUs(state),
  transactionProducts: getGiftsTransactionProducts(state),
  isGiftOffer: getIsGiftOffer(state),
  paymentAuthorisationData: getPaymentAuthorisationData(state),
});

const mapDispatchToProps = {
  resetGifts,
  setExpressDelivery: doSetExpressDelivery,
  requiresPaymentAuthorisation,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(GiftsPaymentAndDelivery);
